<template>
    <router-link :to="tileUri"
        class="prediction"
    >
        <template v-if="sportEvent">
            <div class="prediction__status">
                <status :status="sportEventStatus" />
                <span
                    v-show="showRedDot"
                    class="prediction__red-dot"
                />
            </div>

            <div class="prediction__versus">
                <partner-logo
                    class="prediction__versus-logo"
                    :show-placeholder="!sportEventHomeTeamLogo"
                    :url="sportEventHomeTeamLogo"
                />

                <div class="prediction__versus-details">
                    <span class="prediction__versus-date">{{ sportEventStartDate }}</span>
                    <span class="prediction__versus-time">{{ sportEventStartTime }}</span>
                </div>

                <partner-logo
                    class="prediction__versus-logo"
                    :show-placeholder="!sportEventAwayTeamLogo"
                    :url="sportEventAwayTeamLogo"
                />
            </div>

            <h4 class="prediction__question">{{ sportEventName }}</h4>
            <span class="prediction__season-name">{{ sportEventSeasonName }}</span>
        </template>
    </router-link>
</template>

<script>
// 3rd party
import {
    mapGetters,
} from 'vuex';

// Utils
import {
    format,
} from '@shared/utils/dates.js';

// Components
import partnerLogo from '@webapp/components/partner-logo/partner-logo.vue';
import status from '@webapp/modules/fan-engagement/components/predictor/status.vue';

export default {
    name: 'prediction-tile-v2',
    components: {
        partnerLogo,
        status,
    },
    props: {
        sportEventData: {
            type: Object,
            required: true,
        },
        partnerId: {
            type: String,
            default: '',
        },
        teamId: {
            type: String,
            default: '',
        },
        currencyId: {
            type: String,
            required: true,
        },
        isCarouselSportEvent: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapGetters('predictorSportEvents', [
            'getSportEventById',
            'getSportEventTeamLogoByQualifier',
        ]),

        ...mapGetters('wallet', ['ledgerBalanceByCurrencyId']),

        tileUri() {
            return `/predictor/${this.sportEventId}/team/${this.teamId}`;
        },

        sportEvent() {
            return this.sportEventData;
        },

        sportEventId() {
            return this.sportEvent?.id;
        },

        sportEventName() {
            return this.sportEvent?.name;
        },

        sportEventStatus() {
            return this.sportEvent?.userData?.sportEventProgressionLabel || {};
        },

        date() {
            if (!this.sportEvent) {
                return '';
            }

            return (this.sportEvent.startAt || '').replace(' ', 'T');
        },

        sportEventStartDate() {
            return format(this.date, 'weekdayLongDayShortMonthShort');
        },

        sportEventStartTime() {
            return format(this.date, 'time');
        },

        sportEventSeasonName() {
            return this.sportEvent?.season.name;
        },

        sportEventHomeTeamLogo() {
            return this.getSportEventTeamLogoByQualifier(this.sportEventId, this.partnerId, this.isCarouselSportEvent, 'home') || '';
        },

        sportEventAwayTeamLogo() {
            return this.getSportEventTeamLogoByQualifier(this.sportEventId, this.partnerId, this.isCarouselSportEvent, 'away') || '';
        },

        hasUserVoted() {
            if (!this.sportEvent?.userData?.predictionUserData[this.teamId]?.hasPredicted) {
                return false;
            }

            return Boolean(this.sportEvent.userData.predictionUserData[this.teamId].hasPredicted);
        },

        ledgerBalance() {
            if (!this.sportEvent || !this.currencyId || !this.currencyId.length) {
                return 0;
            }

            return this.ledgerBalanceByCurrencyId(this.currencyId);
        },

        hasBalanceToVote() {
            if (!this.sportEvent || !this.sportEvent.teams || !this.teamId) {
                return false;
            }

            const currentTeam = this.sportEvent.teams.find((team) => team.team.id === this.teamId)?.team;
            const allowPredictionIfNotFan = currentTeam?.metadata?.allowPredictionIfNotFan;

            if (allowPredictionIfNotFan) {
                return true;
            }

            return this.ledgerBalance > 0;
        },

        isPredictionAvailable() {
            if (!this.sportEvent || !this.sportEvent.sportTypeId) {
                return false;
            }

            const predictionsClosed = this.sportEvent.predictionStatus !== 'opened';
            const hasPredictableSportEvents = Boolean(this.sportEvent.predictableSportEvents && this.sportEvent.predictableSportEvents.length);

            return !this.hasUserVoted && !predictionsClosed && hasPredictableSportEvents;
        },

        showRedDot() {
            return this.isPredictionAvailable && this.hasBalanceToVote;
        },

    },
};
</script>

<style lang="scss" scoped>
.prediction {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 38rem;
    height: 23.4rem;
    padding: 1rem 1rem 2rem;
    margin: 2rem;
    border-radius: 0.8rem;
    box-shadow: 0 1rem 1rem rgba($black-color, 0.08);
    background: $white-color;
    font-family: Nunito;
    text-decoration: none;
    text-align: center;
    color: $daintree-color;

    &__red-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        padding: 0;
        background-color: $torch-red-color;
    }

    @include max-width(sm) {
        width: calc(100% - 1rem);
        margin: 0 0 2rem;
    }

    &__status {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
        align-self: flex-start;
        width: 100%;
        margin-bottom: 3rem;
    }

    &__versus {
        display: flex;
        align-items: center;

        &-details {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
        }

        &-date {
            font-size: 1.2rem;
        }

        &-time {
            font-size: 2rem;
            font-weight: 700;
        }

        &-logo {
            width: 6.7rem;
            height: 6.7rem;
            margin: 0 2rem 0 2rem;
        }
    }

    &__question {
        font-size: 1.5rem;
        margin-bottom: 1rem;
    }

    &__season-name {
        font-size: 1.1rem;
        color: $hit-gray-color;
    }
}
</style>